import type { CreateArInvoice } from '@dcc-erp/shared';

// The three FX-related fields on the create-invoice wire shape. All optional —
// THB invoices omit them entirely; foreign-currency invoices send all three.
type FxFields = Pick<CreateArInvoice, 'currencyCode' | 'foreignAmount' | 'exchangeRateAtBooking'>;

/**
 * Build the FX portion of a CreateArInvoice payload.
 *
 * Returns an empty object for THB (or an empty currency code) so the THB
 * submit path stays byte-for-byte unchanged. For any other currency it returns
 * the three FX fields; the service validates that foreignAmount + rate are both
 * populated (> 0) and derives subtotal/VAT in THB from them.
 */
export function buildFxFields(
  currencyCode: string,
  foreignAmount: number,
  rate: number,
): Partial<FxFields> {
  if (!currencyCode || currencyCode === 'THB') return {};
  return { currencyCode, foreignAmount, exchangeRateAtBooking: rate };
}

/**
 * True when a NON-THB invoice is missing the foreign amount or the exchange
 * rate (both must be > 0). Drives the create-invoice submit-disabled guard so
 * the user can't post a foreign-currency invoice the service would reject. THB
 * invoices are never "incomplete" on FX grounds.
 */
export function isFxIncomplete(currencyCode: string, foreignAmount: number, rate: number): boolean {
  return currencyCode !== 'THB' && (foreignAmount <= 0 || rate <= 0);
}
